import React, { useState } from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericRowActionsMenu from "@ui-components/Menu/GenericRowActionsMenu";

export interface Auth0User {
  user_id: string;
  name: string;
  email: string;
  hasMFA: boolean;
  status: string;
  last_login: string;
  roles: Array<{
    id: number;
    name: string;
  }>;
}

interface ActionsDropdownProps {
  row: Auth0User;
  onEdit: (userId: string) => void;
  onDelete: (userId: string) => void;
  onBlock: (userId: string, isBlocked: boolean) => void;
  onResetPassword: (email: string) => void;
  onResetMFA: (userId: string) => void;
  ssoActive: boolean;
}

/** What can be done to a user, in menu order. */
const userActions = (row: Auth0User, ssoActive: boolean): string[] => {
  const actions =
    row.status === "blocked"
      ? ["Edit", "Delete", "Unblock"]
      : ["Edit", "Delete", "Block"];
  if (row.user_id.startsWith("auth0|")) {
    actions.push("Reset Password");
  }
  if (!ssoActive) {
    actions.push("Reset MFA");
  }
  return actions;
};

const runUserAction = (
  action: string,
  row: Auth0User,
  { onEdit, onDelete, onBlock, onResetPassword, onResetMFA }: Omit<ActionsDropdownProps, "row" | "ssoActive">
) => {
  switch (action) {
    case "Edit":
      onEdit(row.user_id);
      break;
    case "Delete":
      onDelete(row.user_id);
      break;
    case "Block":
      onBlock(row.user_id, false);
      break;
    case "Unblock":
      onBlock(row.user_id, true);
      break;
    case "Reset Password":
      onResetPassword(row.email);
      break;
    case "Reset MFA":
      onResetMFA(row.user_id);
      break;
  }
};

/**
 * A user's actions behind "⋯" on a phone card (CTX-1378): the 150 px
 * "Select an option" dropdown took a line of its own on every card.
 */
export function UserActionsMenu({ row, ssoActive, ...handlers }: ActionsDropdownProps) {
  return (
    <GenericRowActionsMenu
      ariaLabel={`Actions for ${row.name || row.email}`}
      actions={userActions(row, ssoActive).map((action) => ({
        id: action,
        label: action,
        onSelect: () => runUserAction(action, row, handlers),
      }))}
    />
  );
}

export function ActionsDropdown({
  row,
  onEdit,
  onDelete,
  onBlock,
  onResetPassword,
  onResetMFA,
  ssoActive,
}: ActionsDropdownProps) {
  const [dropdownKey, setDropdownKey] = useState(0);
  const actions = userActions(row, ssoActive);

  const handleSelect = (action: string) => {
    runUserAction(action, row, { onEdit, onDelete, onBlock, onResetPassword, onResetMFA });
    setDropdownKey((prev) => prev + 1);
  };

  return (
    <GenericDropdown
      key={dropdownKey}
      items={actions}
      onSelect={handleSelect}
      defaultLabel="Select an option"
      widthClass="w-[150px]"
      maxWidthClass="max-w-[150px]"
      className="text-sm text-semantic-text-primary"
      size="sm"
      fixedPosition={true}
    />
  );
}

export const auth0UsersColumns: GenericTableColumn<Auth0User>[] = [
  {
    header: "Full Name",
    accessor: "name",
    renderCell: (row) => row.name,
  },
  {
    header: "Email",
    accessor: "email",
    renderCell: (row) => row.email,
  },
  {
    header: "MFA",
    renderCell: (row) => {
      if (row.hasMFA) {
        return (
          <GenericTag
            label="Configured"
            variant="success"
            size="small"
            rounded="medium"
          />
        );
      } else {
        return (
          <GenericTag
            label="Not Configured"
            variant="error"
            size="small"
            rounded="medium"
          />
        );
      }
    },
  },
  {
    header: "Status",
    renderCell: (row) => {
      const originalStatus = row.status;
      const lowerStatus = row.status.toLowerCase();
      const capitalizedStatus =
        originalStatus.charAt(0).toUpperCase() + originalStatus.slice(1).toLowerCase();

      let variant: "success" | "error" | "warning" | "default" | "neutral" = "default";

      if (lowerStatus === "active") {
        variant = "success";
      } else if (lowerStatus === "blocked") {
        variant = "warning";
      }

      return (
        <GenericTag
          label={capitalizedStatus}
          variant={variant}
          size="small"
          rounded="medium"
        />
      );
    },
  },
  {
    header: "Role",
    renderCell: (row) => row.roles.map((r) => r.name).join(", "),
  },
  {
    header: "Last login",
    renderCell: (row) => {
      if (!row.last_login) return "—";
      const date = new Date(row.last_login);
      return date.toLocaleString("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    },
  },
  {
    header: "Actions",
  },
];
